import { type Entry, type EntryJSON, type MutableEntry } from '@databases/model/entry';
import { FieldType } from '@databases/model/field';
import { type AllValueJSON, type MutableValue } from '@databases/model/value';
import * as Y from 'yjs';

import { type DefaultValueConverters } from './default-value-converters.ts';
import { SynchronizedMap } from './helpers/synchronized-map.ts';
import { type DisposableElement, type WrappedYType } from './helpers/types.ts';
import { type YValueType } from './values/types.ts';
import { YAITextValue } from './values/y-ai-text-value.ts';
import { YAttachmentValue } from './values/y-attachment-value.ts';
import { YCheckboxValue } from './values/y-checkbox-value.ts';
import { YDateValue, YDateValueWithoutCurrent } from './values/y-date-value.ts';
import { YEntryLinkValue } from './values/y-entry-link-value.ts';
import { YHyperlinkValue } from './values/y-hyperlink-value.ts';
import { YJiraIssueValue } from './values/y-jira-issue-value.ts';
import { YNumberValue } from './values/y-number-value.ts';
import { YPageLinkValue } from './values/y-page-link-value.ts';
import { YSelectValue } from './values/y-select-value.ts';
import { YTextValue } from './values/y-text-value.ts';
import { YUserValue, YUserValueWithoutCurrent } from './values/y-user-value.ts';
import { FieldMapping, YFieldType } from './y-field.ts';

export const ValueProperties = {
  type: 't',
  value: 'v'
};

export const valueForType = (
  input: Y.Map<any>,
  type: YFieldType,
  isDefaultEntry: boolean,
  defaultValueConverters: DefaultValueConverters
): YValueType | undefined => {
  switch (type) {
    case YFieldType.TEXT:
      return new YTextValue<FieldType.Text>(input);
    case YFieldType.AI_SUMMARY:
      return new YAITextValue<FieldType.AiSummary>(input, undefined, FieldType.AiSummary);
    case YFieldType.DATE:
      return isDefaultEntry ? new YDateValue(input) : new YDateValueWithoutCurrent(input);
    case YFieldType.NUMBER:
      return new YNumberValue(input);
    case YFieldType.SELECT:
      return new YSelectValue(input);
    case YFieldType.ATTACHMENT:
      return new YAttachmentValue(input);
    case YFieldType.CHECKBOX:
      return new YCheckboxValue(input);
    case YFieldType.USER:
      return isDefaultEntry
        ? new YUserValue(input)
        : new YUserValueWithoutCurrent(input, defaultValueConverters.getCurrentUser);
    case YFieldType.PAGE_LINK:
      return new YPageLinkValue(input);
    case YFieldType.HYPERLINK:
      return new YHyperlinkValue(input);
    case YFieldType.ENTRY_LINK:
      return new YEntryLinkValue(input);
    case YFieldType.JIRA_ISSUE:
      return new YJiraIssueValue(input);
  }
};

export const valueFromJSON = (
  json: AllValueJSON,
  isDefaultEntry: boolean,
  defaultValueConverters: DefaultValueConverters
): Y.AbstractType<any> => {
  switch (json.type) {
    case FieldType.Text:
      return YTextValue.fromJSON(json).unwrap();
    case FieldType.AiSummary:
      return YAITextValue.fromAiTextJSON(json).unwrap();
    case FieldType.Hyperlink:
      return YHyperlinkValue.fromJSON(json).unwrap();
    case FieldType.Date:
      return isDefaultEntry
        ? YDateValue.fromJSON(json).unwrap()
        : YDateValueWithoutCurrent.fromJSON(json).unwrap();
    case FieldType.Number:
      return YNumberValue.fromJSON(json).unwrap();
    case FieldType.Checkbox:
      return YCheckboxValue.fromJSON(json).unwrap();
    case FieldType.Select:
      return YSelectValue.fromJSON(json).unwrap();
    case FieldType.User:
      return isDefaultEntry
        ? YUserValue.fromJSON(json).unwrap()
        : YUserValueWithoutCurrent.fromJSON(
            json,
            defaultValueConverters.getCurrentUser
          ).unwrap();
    case FieldType.Attachment:
      return YAttachmentValue.fromJSON(json).unwrap();
    case FieldType.EntryLink:
      return YEntryLinkValue.fromJSON(json).unwrap();
    case FieldType.JiraIssue:
      return YJiraIssueValue.fromJSON(json).unwrap();
    case FieldType.PageLink:
      return YPageLinkValue.fromJSON(json).unwrap();
  }
};

export class YEntry implements Entry, MutableEntry, DisposableElement, WrappedYType {
  #values: SynchronizedMap<YValueType>;

  constructor(
    readonly data: Y.Map<unknown>,
    private isDefaultEntry: boolean,
    private defaultValueConverters: DefaultValueConverters
  ) {
    this.#values = new SynchronizedMap(data, (input) => {
      if (!(input instanceof Y.Map)) {
        return undefined;
      }

      const value = valueForType(
        input,
        input.get(ValueProperties.type),
        isDefaultEntry,
        defaultValueConverters
      );
      return value;
    });
  }

  observeDeep(cb: () => void) {
    this.data.observeDeep(cb);
  }

  unobserveDeep(cb: () => void) {
    this.data.unobserveDeep(cb);
  }

  setValue<T extends FieldType>(fieldId: string, type: T): MutableValue<T> {
    if (
      !this.#values.data[fieldId] ||
      (this.#values.data[fieldId] && type !== this.#values.data[fieldId].type)
    ) {
      const value = valueForType(
        new Y.Map([[ValueProperties.type, FieldMapping[type]]]),
        FieldMapping[type],
        this.isDefaultEntry,
        this.defaultValueConverters
      )!;

      this.#values.set(fieldId, value);

      return value as unknown as MutableValue<T>; // FIXME: can we improve this type?
    } else {
      return this.#values.data[fieldId] as unknown as MutableValue<T>; // FIXME: can we improve this type?
    }
  }

  getValue(fieldId: string) {
    return this.#values.data[fieldId];
  }

  removeValue(fieldId: string) {
    this.#values.delete(fieldId);
  }

  toJSON(): EntryJSON {
    return {
      values: Object.keys(this.#values.data).reduce(
        (acc, key) => {
          acc[key] = this.#values.data[key].toJSON();
          return acc;
        },
        {} as Record<string, AllValueJSON>
      )
    };
  }

  static fromJSON(
    json: EntryJSON,
    isDefaultEntry: boolean,
    defaultValueConverters: DefaultValueConverters
  ) {
    const entries: Array<[string, Y.AbstractType<unknown>]> = [];
    Object.keys(json.values).map((fieldId) => {
      entries.push([
        fieldId,
        valueFromJSON(json.values[fieldId], isDefaultEntry, defaultValueConverters)
      ]);
    });

    return new YEntry(new Y.Map(entries), isDefaultEntry, defaultValueConverters);
  }

  dispose(): void {
    this.#values.dispose();
  }

  unwrap(): Y.AbstractType<any> | undefined {
    return this.#values.unwrap();
  }

  reset(): void {
    this.data.delete(ValueProperties.type);
    this.data.delete(ValueProperties.value);
  }
}
